/**
 * TaskLineInput — one editable task line on the notebook paper.
 *
 * Rendered by the shared PrepTaskListEditor, which BOTH prep-request task
 * surfaces use so they behave identically:
 *   - SubmitPrepRequest.jsx  (entering tasks at submission)
 *   - PrepRequestDetail.jsx  (the Adviser amending tasks afterwards, §26.7)
 *
 * The wrapping / auto-grow behaviour and the keyboard conventions are
 * defined here once so amending a task feels exactly like typing one in
 * the first place.
 */

import React, { useRef, useLayoutEffect } from 'react';

/**
 * One task line on the notebook — a textarea that wraps and auto-grows
 * instead of scrolling a long task sideways on a single line. It starts one
 * ruled line tall and gains a ruled line for every wrapped line, shrinking
 * back again as text is deleted (the .np-task-input rules keep each line at
 * the paper's 36px rule spacing).
 *
 * Keyboard (outliner conventions, §26.2.2):
 *   Enter            → onEnter (a new line at the same level); never a newline
 *   Shift+Enter      → a line break inside the task being written
 *   Tab              → onTab (demote to a sub-task); the browser's focus
 *                      move is suppressed while the editor is focused
 *   Shift+Tab        → onShiftTab (promote to top level)
 *   Backspace, empty → onBackspaceEmpty (remove the line, focus the previous)
 */
export default function TaskLineInput({
  value,
  onChange,
  onEnter,
  onTab,
  onShiftTab,
  onBackspaceEmpty,
  focusRequested,
  onFocusHandled,
  placeholder,
  disabled = false,
  ariaLabel,
}) {
  const ref = useRef(null);

  // Fit the box to its content. Keyed on `value` so it also runs on the very
  // first render — a task that arrives already filled in (a restored draft, or
  // a review handoff) shows at full height immediately, not only once typed in.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  // Focus handed over from another line; the caret goes to the end so
  // typing continues naturally.
  useLayoutEffect(() => {
    if (!focusRequested) return;
    const el = ref.current;
    if (el) {
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
    if (onFocusHandled) onFocusHandled();
  }, [focusRequested, onFocusHandled]);

  return (
    <textarea
      ref={ref}
      className="np-input np-task-input"
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          // Plain Enter never leaves a newline in the task text — it
          // starts the next line of the numbered list.
          e.preventDefault();
          if (onEnter) onEnter();
          return;
        }
        if (e.key === 'Tab') {
          // Outliner indent / outdent; the default focus move is suppressed.
          e.preventDefault();
          if (e.shiftKey) { if (onShiftTab) onShiftTab(); }
          else if (onTab) onTab();
          return;
        }
        if (e.key === 'Backspace' && (value || '').length === 0 && onBackspaceEmpty) {
          e.preventDefault();
          onBackspaceEmpty();
        }
      }}
      placeholder={placeholder}
      autoComplete="off"
      disabled={disabled}
      aria-label={ariaLabel}
    />
  );
}
